/**
 * Processing Service for RunPod job management
 * Handles cropping and captioning operations
 */

import { imagesAPI } from '../api/apiService';
import runpodService from '../runpod/runpodService';

class ProcessingService {
  constructor() {
    this.activeJobs = new Map();
  }

  /**
   * Sanitize filename by replacing spaces with underscores
   * This should match what the cropper does when saving files
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   */
  sanitizeFilename(filename) {
    return filename.replace(/\s+/g, '_');
  }

  /**
   * Crop images for an influencer
   * @param {string} influencerId - Influencer ID
   * @param {Array} images - Array of image objects with s3Key and filename
   * @param {string} sex - 'male' or 'female' prompt for cropping
   * @param {function} onProgress - Progress callback
   * @returns {Promise} Job result
   */
  async cropImages(influencerId, images, sex = 'female', onProgress = null) {
    try {
      // Extract S3 bucket from first image's s3Key (format: bucket/path/file)
      const s3Bucket = process.env.REACT_APP_S3_BUCKET_IMAGES || 'ohlyfans-images';

      // Prepare images with S3 info
      const preparedImages = images.map(img => ({
        s3Bucket: s3Bucket,
        s3Key: img.s3Key || `${influencerId}/raw/${img.filename}`,
        filename: img.filename || img.s3Key.split('/').pop()
      }));

      if (onProgress) {
        onProgress({ status: 'starting', total: preparedImages.length, completed: 0 });
      }

      // Call RunPod cropping service with progress callback
      const response = await runpodService.cropImages(preparedImages, influencerId, sex, (cropProgress) => {
        if (onProgress) {
          onProgress({
            status: 'processing',
            total: cropProgress.total,
            completed: cropProgress.completed,
            message: cropProgress.message
          });
        }
      });

      // Store job info
      if (response.jobId) {
        this.activeJobs.set(response.jobId, {
          type: 'crop',
          influencerId,
          totalImages: preparedImages.length,
          startTime: Date.now()
        });
      }

      if (onProgress) {
        onProgress({ status: 'submitted', jobId: response.jobId, total: preparedImages.length });
      }

      // Automatically trigger captioning after successful cropping
      if (response.success || response.results) {
        console.log('Cropping successful, registering images in database...');

        try {
          // Register the cropped images in the database
          const imagesToRegister = preparedImages.map(img => ({
            influencer_id: influencerId,
            s3_key: `${influencerId}/cropped/${this.sanitizeFilename(img.filename)}`,
            folder: 'cropped',
            filename: this.sanitizeFilename(img.filename),
            source: 'runpod',
            metadata: {
              original_key: img.s3Key,
              crop_prompt: sex,
              upload_method: 'external',
              processed_by: 'runpod_cropper'
            }
          }));

          console.log('📝 Registering cropped images in database:', {
            count: imagesToRegister.length,
            influencer_id: influencerId,
            folder: 'cropped',
            source: 'runpod'
          });
          console.log('Full registration payload:', JSON.stringify(imagesToRegister, null, 2));

          const registrationResult = await imagesAPI.registerImages(imagesToRegister, true); // verify_s3=true to get size/content_type
          console.log('✅ Cropped images registered successfully:', registrationResult);

          // Wait a moment for S3 to propagate
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Get list of cropped images to caption
          // Use sanitized filenames to match what the cropper saved
          const croppedImages = preparedImages.map(img => ({
            ...img,
            s3Key: `${influencerId}/cropped/${this.sanitizeFilename(img.filename)}`
          }));

          const captionResponse = await this.captionImages(influencerId, croppedImages, 'cropped', (captionProgress) => {
            if (onProgress) {
              onProgress({
                ...captionProgress,
                status: 'captioning',
                cropJobId: response.jobId
              });
            }
          });

          return {
            ...response,
            captionJobId: captionResponse.jobId,
            captioning: true
          };
        } catch (error) {
          console.error('Error in post-crop processing:', error);
          // Return crop success even if registration/captioning fails
          return {
            ...response,
            error: error.message
          };
        }
      }

      return response;
    } catch (error) {
      console.error('Error starting crop job:', error);
      throw error;
    }
  }

  /**
   * Caption images for an influencer
   * @param {string} influencerId - Influencer ID
   * @param {Array} images - Array of images to caption
   * @param {string} folder - Folder where images are located ('raw' or 'cropped')
   * @param {function} onProgress - Progress callback
   * @returns {Promise} Job result
   */
  async captionImages(influencerId, images, folder = 'cropped', onProgress = null) {
    try {
      const s3Bucket = process.env.REACT_APP_S3_BUCKET_IMAGES || 'ohlyfans-images';

      // For caption-only on raw images, we'll caption them in place
      // The backend should handle any necessary copying
      let targetFolder = folder;
      if (folder === 'raw') {
        console.log('Captioning raw images - captions will be saved in raw folder');
        if (onProgress) {
          onProgress({
            status: 'preparing',
            message: 'Preparing images for captioning...',
            total: images.length,
            completed: 0
          });
        }

        // Keep images in raw folder for captioning
        // The captions will be saved as .txt files alongside the images
        targetFolder = 'raw';
        console.log('Images will be captioned in their current location');
      }

      // Prepare images with S3 info (pointing to the target folder)
      const preparedImages = images.map(img => ({
        s3Bucket: s3Bucket,
        s3Key: img.s3Key || `${influencerId}/${targetFolder}/${img.filename}`,
        filename: img.filename || img.s3Key?.split('/').pop()
      }));

      if (onProgress) {
        onProgress({
          status: 'starting',
          message: 'Starting captioning...',
          total: preparedImages.length,
          completed: 0
        });
      }

      // Call RunPod captioning service with progress callback
      const response = await runpodService.captionImages(preparedImages, influencerId, targetFolder, (captionProgress) => {
        if (onProgress) {
          onProgress({
            status: 'processing',
            total: captionProgress.total,
            completed: captionProgress.completed,
            message: captionProgress.message
          });
        }
      });

      // Store job info
      if (response.jobId) {
        this.activeJobs.set(response.jobId, {
          type: 'caption',
          influencerId,
          totalImages: preparedImages ? preparedImages.length : 'all',
          startTime: Date.now()
        });
      }

      if (onProgress) {
        onProgress({
          status: 'submitted',
          jobId: response.jobId,
          total: preparedImages ? preparedImages.length : 'all'
        });
      }

      // Register the images in database if captioning was successful
      if (response.success || response.results) {
        console.log('Captioning successful, registering images in database...');

        try {
          const imagesToRegister = preparedImages.map(img => ({
            influencer_id: influencerId,
            s3_key: img.s3Key,
            folder: targetFolder,
            filename: img.filename,
            source: 'runpod',
            metadata: {
              captioned: true,
              upload_method: 'external',
              processed_by: 'runpod_captioner',
              caption_folder: targetFolder
            }
          }));

          console.log('📝 Registering captioned images in database:', {
            count: imagesToRegister.length,
            influencer_id: influencerId,
            folder: targetFolder,
            source: 'runpod'
          });
          console.log('Full registration payload:', JSON.stringify(imagesToRegister, null, 2));

          const registrationResult = await imagesAPI.registerImages(imagesToRegister, true); // verify_s3=true to get size/content_type
          console.log('✅ Captioned images registered successfully:', registrationResult);
        } catch (regError) {
          console.error('Error registering captioned images:', regError);
          // Don't fail the whole operation if registration fails
        }
      }

      return response;
    } catch (error) {
      console.error('Error starting caption job:', error);
      throw error;
    }
  }

  /**
   * Get active jobs
   * @returns {Array} List of active jobs
   */
  getActiveJobs() {
    return Array.from(this.activeJobs.values()).filter(job => !job.endTime);
  }

  /**
   * Clear completed jobs
   */
  clearCompletedJobs() {
    for (const [jobId, job] of this.activeJobs.entries()) {
      if (job.endTime) {
        this.activeJobs.delete(jobId);
      }
    }
  }
}

export const processingService = new ProcessingService();
export default processingService;